// #297 — desktop (system) notifications for live alerts. The user allows them once (from a click: Firefox
// and Safari only ask from a user gesture); the browser remembers the answer across reloads, and the OS
// shows each one with its own notification sound, also while the tab is hidden or the window minimised.
// Desktop browsers only: phones get the mobile app's push, and Android Chrome cannot construct a page
// notification at all (it needs a service worker). Nothing here throws: where the API is missing or
// refuses, the feature is simply absent.
import type { DesktopAlert, DesktopAlertPermission } from '@/types'

type NotificationApi = typeof Notification
// `image` is not in TypeScript's DOM types; Chrome on Windows / Linux / ChromeOS shows it, others ignore it.
type Options = NotificationOptions & { image?: string }

const ICON = '/detercam.png'
const listeners = new Set<() => void>()
// The browser has the API but refused to construct one (Android Chrome "Illegal constructor").
let broken = false
// Whether the constructor was tried once before the user was asked (see canConstruct).
let probed = false

function isPhoneOrTablet(): boolean {
  if (typeof navigator === 'undefined') return false
  const nav = navigator as Navigator & { userAgentData?: { mobile?: boolean } }
  if (nav.userAgentData?.mobile) return true
  return /Android|iPhone|iPad|iPod|Mobi/i.test(nav.userAgent ?? '')
}

// An Android tablet in "desktop site" mode sends a desktop user agent, yet its Chrome refuses every page
// notification. So, before the user is asked, construct one once: while permission is not granted a desktop
// browser never shows it (it only fires `error`); a browser that throws here could never show one.
function canConstruct(N: NotificationApi): boolean {
  try {
    new N('').close()
    return true
  } catch {
    return false
  }
}

function api(): NotificationApi | undefined {
  if (broken || typeof window === 'undefined') return undefined
  const N = (window as unknown as { Notification?: NotificationApi }).Notification
  if (typeof N !== 'function' || typeof N.requestPermission !== 'function') return undefined
  if (isPhoneOrTablet()) return undefined
  // Only while unanswered: once granted, a test construction would show an empty notification.
  if (!probed && N.permission === 'default') {
    probed = true
    if (!canConstruct(N)) { broken = true; return undefined }
  }
  return N
}

function notify() { listeners.forEach((l) => l()) }

export function isDesktopAlertSupported(): boolean {
  return !!api()
}

export function getDesktopAlertPermission(): DesktopAlertPermission {
  const N = api()
  return N ? N.permission : 'unsupported'
}

export function subscribeDesktopAlerts(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/** D2 — ask the browser. Call it straight from a click (the browser only asks from a user gesture).
 * Once the user has answered, it never asks again: "denied" can only be changed in the browser's site
 * settings. Resolves with the permission after the answer. */
export function requestDesktopAlertPermission(): Promise<DesktopAlertPermission> {
  const N = api()
  if (!N) return Promise.resolve('unsupported')
  if (N.permission !== 'default') {
    notify()   // answered meanwhile (another tab): a prompt still on screen here goes away
    return Promise.resolve(N.permission)
  }
  return new Promise((resolve) => {
    let settled = false
    const done = () => {
      if (settled) return
      settled = true
      notify()
      resolve(getDesktopAlertPermission())
    }
    try {
      // Older Safari only takes the callback; current browsers return a promise (and may call both).
      const answer = N.requestPermission(() => done()) as Promise<NotificationPermission> | undefined
      if (answer && typeof answer.then === 'function') answer.then(done, done)
    } catch {
      done()
    }
  })
}

/** D3 — only when the toast and the chime are not enough: the page is not in front (tab hidden, window
 * minimised), or the chime is still blocked (no click since the reload). Never a double announcement. */
export function shouldShowDesktopAlert(chimeReady: boolean): boolean {
  if (typeof document === 'undefined') return false
  return document.visibilityState === 'hidden' || !chimeReady
}

/** D4 + D6 — show one desktop notification if the user allowed them. A repeat for the same event replaces
 * the earlier one (tag); a normal alert closes by itself (the OS default), a critical one stays until
 * clicked. A click brings the dashboard to the front and runs `onOpen`. Returns null when nothing shows. */
export function showDesktopAlert(alert: DesktopAlert): Notification | null {
  const N = api()
  if (!N || N.permission !== 'granted') return null
  const options: Options = { body: alert.body, icon: ICON }
  if (alert.eventId !== undefined) options.tag = String(alert.eventId)
  if (alert.image) options.image = alert.image
  if (alert.critical) options.requireInteraction = true
  try {
    const n = new N(alert.title, options)
    n.onclick = () => {
      try { window.focus() } catch { /* the browser may refuse; the notification still opens the event */ }
      alert.onOpen?.()
      n.close()
    }
    return n
  } catch {
    // Android Chrome and other browsers that only show notifications from a service worker: the feature
    // goes away (the header prompt hides) instead of failing on every alert.
    broken = true
    notify()
    return null
  }
}

/** Tests only: forget a refused constructor, the one-time check and the subscribers. */
export function __resetDesktopAlertsForTests() {
  broken = false
  probed = false
  listeners.clear()
}
